import { useState, useEffect, useCallback, useMemo } from "react";
import { backend } from "@/services/backend";
import {
  CollectionDataCache,
  CollectionStandardData,
  fetchData as fetchJson,
} from "@/types/collection";
import {
  LeaderboardItemType,
  LeaderboardMetric,
  MenuType,
} from "@/types/leader-board";
import { OwnerRatingRow } from "@/services/types";
import { calcLevelByExperience } from "@/utils/experience";
import {
  LEADERBOARD_CONSTANTS,
  LEADERBOARD_TEXT,
  METRIC_TO_PARAM_MAP,
} from "@/constants/leaderboard";
import { NftRatingData, ratingBackend } from "@/services/ratingBackend";
import {
  fetchOwnerNftData,
  fetchOwnerCollectionData,
  fetchOwnerUserData,
} from "@/utils/leaderboardDataFetcher";

interface UseLeaderboardDataProps {
  selectedMenu: MenuType;
  metric: LeaderboardMetric;
  limit?: number;
  isConnected: boolean;
  walletAddress: string | null;
}

interface UseLeaderboardDataReturn {
  data: LeaderboardItemType[];
  myData: LeaderboardItemType | null;
  isLoading: boolean;
  error: string | null;
  loadMore: () => void;
  hasMore: boolean;
  refetch: () => void;
}

export function useLeaderboardData({
  selectedMenu,
  metric,
  limit = LEADERBOARD_CONSTANTS.DEFAULT_LIMIT,
  isConnected,
  walletAddress,
}: UseLeaderboardDataProps): UseLeaderboardDataReturn {
  const [data, setData] = useState<LeaderboardItemType[]>([]);
  const [myData, setMyData] = useState<LeaderboardItemType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const resetState = useCallback(() => {
    setOffset(0);
    setData([]);
    setMyData(null);
    setHasMore(true);
    setError(null);
  }, []);

  const getUserDisplayName = (user: OwnerRatingRow): string => {
    if (user.username) {
      return user.username;
    }
    return `${"USER#"}${user.address}`;
  };

  const getUserMetricValue = (
    user: OwnerRatingRow,
    metric: LeaderboardMetric
  ): number => {
    switch (metric) {
      case "volume":
        return Number(user.volume || 0) / LEADERBOARD_CONSTANTS.TON_DIVISOR;
      case "level":
        return calcLevelByExperience(user.experience);
      case "all_stars":
      default:
        return Math.floor(Number(user.stars || 0));
    }
  };

  const transformCollectionData = useCallback(
    (
      collections: (CollectionDataCache & { _avatar: string; name: string })[],
      currentOffset: number
    ): LeaderboardItemType[] => {
      return collections.map((collection, index) => {
        const value =
          metric === "volume"
            ? Number(collection.volume || 0) / LEADERBOARD_CONSTANTS.TON_DIVISOR
            : Number(collection.stars || 0);

        return {
          rank: currentOffset + index + 1,
          name: collection.name || LEADERBOARD_TEXT.UNKNOWN_NAME,
          value,
          address: collection.address,
          avatar: collection._avatar || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
          ownerAddress: collection.owner,
        };
      });
    },
    [metric]
  );

  const transformUsersData = useCallback(
    (users: OwnerRatingRow[], currentOffset: number): LeaderboardItemType[] => {
      return users.map((user, index) => ({
        rank: currentOffset + index + 1,
        name: getUserDisplayName(user),
        value: getUserMetricValue(user, metric),
        address: user.address,
        avatar: user.avatar || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
        ownerAddress: user.address,
      }));
    },
    [metric]
  );

  const transformNftsData = useCallback(
    async (
      nfts: NftRatingData[],
      currentOffset: number
    ): Promise<LeaderboardItemType[]> => {
      const transformedNfts = await Promise.all(
        nfts.map(async (nft, index) => {
          try {
            const meta = await fetchJson(nft.dataUrl);
            return {
              rank: currentOffset + index + 1,
              name: meta.name || LEADERBOARD_TEXT.UNKNOWN_NAME,
              value: Number(nft.stars || 0),
              address: nft.address,
              avatar: meta.image || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
              indexInCollection: nft.indexInCollection,
              ownerAddress: nft.owner,
            };
          } catch (error) {
            console.warn(
              `Failed to fetch metadata for NFT ${nft.address}:`,
              error
            );
            return {
              rank: currentOffset + index + 1,
              name: LEADERBOARD_TEXT.UNKNOWN_NAME,
              value: Number(nft.stars || 0),
              address: nft.address,
              avatar: LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
              indexInCollection: nft.indexInCollection,
              ownerAddress: nft.owner,
            };
          }
        })
      );
      return transformedNfts;
    },
    []
  );

  const fetchCollectionMetadata = useCallback(
    async (
      collections: CollectionDataCache[]
    ): Promise<(CollectionDataCache & { _avatar: string; name: string })[]> => {
      return Promise.all(
        collections.map(async collection => {
          try {
            const meta: CollectionStandardData = await fetchJson(
              collection.data_url
            );
            return {
              ...collection,
              _avatar: meta.image || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
              name: meta.name || LEADERBOARD_TEXT.UNKNOWN_NAME,
            };
          } catch (error) {
            console.warn(
              `Failed to fetch metadata for collection ${collection.address}:`,
              error
            );
            return {
              ...collection,
              _avatar: LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
              name: LEADERBOARD_TEXT.UNKNOWN_NAME,
            };
          }
        })
      );
    },
    []
  );

  const fetchMyData = useCallback(async () => {
    if (!isConnected || !walletAddress) {
      setMyData(null);
      return;
    }

    try {
      if (selectedMenu === "nft") {
        await fetchMyNftData(walletAddress);
      } else if (selectedMenu === "collections") {
        await fetchMyCollectionData(walletAddress);
      } else {
        await fetchMyUserData(walletAddress);
      }
    } catch (error) {
      console.error("Error fetching my data:", error);
      setMyData(null);
    }
  }, [isConnected, walletAddress, selectedMenu, metric]);

  const fetchMyNftData = async (walletAddress: string): Promise<void> => {
    const myNftData = await fetchOwnerNftData(walletAddress);
    setMyData(myNftData);
  };

  const fetchMyCollectionData = async (
    walletAddress: string
  ): Promise<void> => {
    const myCollectionData = await fetchOwnerCollectionData(
      walletAddress,
      metric
    );
    setMyData(myCollectionData);
  };

  const fetchMyUserData = async (walletAddress: string): Promise<void> => {
    const myUserData = await fetchOwnerUserData(walletAddress, metric);
    setMyData(myUserData);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (selectedMenu === "nft") {
        await fetchNftLeaderboard();
      } else if (selectedMenu === "users") {
        await fetchUsersLeaderboard();
      } else if (selectedMenu === "collections") {
        await fetchCollectionsLeaderboard();
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedMenu, metric, offset, limit]);

  const fetchNftLeaderboard = async (): Promise<void> => {
    const currentOffset = offset;
    const nfts = await ratingBackend.getTopNfts({
      param: "stars",
      limit,
      offset: currentOffset,
    });
    const transformedData = await transformNftsData(nfts, currentOffset);
    updateDataState(transformedData, nfts.length === limit, currentOffset);
  };

  const fetchUsersLeaderboard = async (): Promise<void> => {
    const currentOffset = offset;
    const param = METRIC_TO_PARAM_MAP[metric];
    const users = await backend.getTopWalletsRating({
      param,
      limit,
      offset: currentOffset,
    });
    const transformedData = transformUsersData(users, currentOffset);
    updateDataState(transformedData, users.length === limit, currentOffset);
  };

  const fetchCollectionsLeaderboard = async (): Promise<void> => {
    const currentOffset = offset;
    const orderBy = metric === "volume" ? "volume" : "stars";
    const collections = await backend.getAllLastCollectionsWithOffset(
      currentOffset,
      limit,
      false,
      orderBy
    );
    const collectionsWithMetadata = await fetchCollectionMetadata(collections);
    const transformedData = transformCollectionData(
      collectionsWithMetadata,
      currentOffset
    );
    updateDataState(
      transformedData,
      collections.length === limit,
      currentOffset
    );
  };

  const updateDataState = (
    transformedData: LeaderboardItemType[],
    hasMoreData: boolean,
    currentOffset: number
  ): void => {
    setData(prev => {
      // Always replace if offset is 0 or if data is empty (reset scenario)
      if (currentOffset === 0 || prev.length === 0) {
        return transformedData;
      }
      // Otherwise append for pagination
      return [...prev, ...transformedData];
    });
    setHasMore(hasMoreData);
  };

  const handleFetchError = (err: unknown): void => {
    const errorMessage =
      err instanceof Error ? err.message : "Failed to fetch leaderboard data";

    console.error("Error fetching leaderboard data:", err);
    setError(errorMessage);
    setData([]);
  };

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setOffset(prev => prev + limit);
    }
  }, [isLoading, hasMore, limit]);

  const refetch = useCallback(() => {
    resetState();
  }, [resetState]);

  useEffect(() => {
    resetState();
  }, [selectedMenu, metric, resetState]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchMyData();
  }, [fetchMyData]);

  return useMemo(
    () => ({
      data,
      myData,
      isLoading,
      error,
      loadMore,
      hasMore,
      refetch,
    }),
    [data, myData, isLoading, error, loadMore, hasMore, refetch]
  );
}
